import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { GlobalApplicationState } from 'globalApplicationState';
import { DelayType, Notifications, Reminder, ReminderChannel, ReminderOption } from "./models";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Drawer from "@mui/material/Drawer";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { InfoOutlined } from "@mui/icons-material";
import Disclaimer from '../disclaimer';
import { getNotificationChannelsStrings, isCustomChannelAndNoReminderOptionsAreOn } from 'utils/notificationsHelper';

class Reminders extends React.Component<PropsWithRedux, ComponentState> {
  constructor(props: PropsWithRedux) {
    super(props);
    
    this.state = {
      reminders: props.isPreviouslyPublished ? this.getReminders() : [this.getDefaultReminder()],
      isNoAuthoringNotificationChannelsOn: getNotificationChannelsStrings(this.props.notifications).length === 0,
    };
  }

  public componentDidUpdate(prevProps: PropsWithRedux) {
    if (this.props.show && !prevProps.show) {
      this.setState({
        reminders: !!this.props.notifications ? this.getReminders() : [this.getDefaultReminder()],
        isNoAuthoringNotificationChannelsOn: getNotificationChannelsStrings(this.props.notifications).length === 0
      });
    }
  }

  public render() {
    if (!this.props.show || !this.props.notifications || !this.props.notificationSettings)
      return <React.Fragment></React.Fragment>;

    const { contentType } = this.props;

    const settings = this.props.notificationSettings.settings;
    const reminderSettingsEnabled = settings[`${contentType}ReminderEmailAttempt`]?.toggle || settings[`${contentType}ReminderMobileAttempt`]?.toggle || settings[`${contentType}ReminderSMSAttempt`]?.toggle || settings[`${contentType}ReminderTeamsAttempt`]?.toggle;

    if (!reminderSettingsEnabled)
      return <React.Fragment></React.Fragment>;
    
    return (
      <Drawer anchor="right" open={true} onClose={this.onClose} classes={{ paper: "authoring-drawer-panel" }}>
        <Paper square elevation={0} className="authoring-drawer-panel-header">
          <IconButton onClick={this.onClose} size="large">
            <ArrowBackIcon />
          </IconButton>
          <div>Edit Reminders</div>
        </Paper>
        <div className="authoring-drawer-panel-content">
          <Paper elevation={0} className="publishing-reminders">
            <div className="drawer-panel-section-header">
              <Disclaimer
                icon={<InfoOutlined htmlColor="#e6911a" />}
                text={
                  <div>
                      {this.props.isCompliance
                          ? "Set up reminders to employees who have not complied with the post and customize the channels you want to use for each reminder."
                          : "Set up reminders to employees who missed the post and customize the channels you want to use for each reminder."}
                  </div>
              }
              />
            </div>
            <Collapse in={!!this.state.reminders.length}>
              <div>
                <div className="reminders">
                  {this.state.reminders.map((reminder, index) =>
                    <Collapse key={index} in={true} appear={index !== 0} classes={{ wrapperInner: "reminder" }}>
                      <Paper elevation={0} className="reminder-setting">
                        <div className="reminder-label">Reminder {index + 1}</div>
                        <div>
                          <div className="reminder-time">
                            <div>
                                {this.props.isCompliance
                                    ? "If not complied with, send reminder after"
                                    : "If unread, send reminder after"}
                            </div>
                            <TextField
                              variant="outlined"
                              type="number"
                              size="small"
                              placeholder="Enter no."
                              value={reminder.delay || ""}
                              onChange={(ev) => this.onChangeReminderDelay(index, ev.target.value as unknown as number)}
                              className="reminder-time-input"
                              error={!reminder.delay || reminder.delay < 1}
                            />
                            <FormControl size="small">
                              <Select
                                variant="outlined"
                                value={reminder.delayType}
                                onChange={(ev) => this.onChangeReminderDelayType(index, ev.target.value as DelayType)}
                                className="reminder-time-type"
                              >
                                <MenuItem value="hours">hours</MenuItem>
                                <MenuItem value="days">days</MenuItem>
                              </Select>
                            </FormControl>
                          </div>
                          <div>
                            <RadioGroup>
                              <FormControlLabel
                                control={
                                  <Radio
                                    color="primary"
                                    checked={!reminder.customChannels}
                                    onClick={() => this.onSetNotificationReminderChannels(index)}
                                  />
                                }
                                label={`Use channels selected in ${this.props.contentType} publishing notifications`}
                                disabled={this.state.isNoAuthoringNotificationChannelsOn}
                              />
                              {this.getInheritChannelsLabel(reminder)}
                              <FormControlLabel
                                control={
                                  <Radio
                                    color="primary"
                                    checked={reminder.customChannels}
                                    onClick={() => this.onSetCustomReminderChannels(index)}
                                  />
                                }
                                label="Custom channel options"
                              />
                            </RadioGroup>
                            <Collapse in={reminder.customChannels}>
                              <div className="custom-reminder">
                                {settings[`${contentType}ReminderEmailAttempt`]?.toggle &&
                                  <div>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          color="primary"
                                          checked={reminder.emailRange !== "none"}
                                          onChange={(ev, checked) => this.onChangeReminderChannel(checked, index, "email")}
                                        />
                                      }
                                      label="Email"
                                    />
                                  </div>
                                }
                                {settings[`${contentType}ReminderMobileAttempt`]?.toggle &&
                                  <div>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          color="primary"
                                          checked={reminder.mobileRange !== "none"}
                                          onChange={(ev, checked) => this.onChangeReminderChannel(checked, index, "mobile")}
                                        />
                                      }
                                      label="Mobile App"
                                    />
                                  </div>
                                }
                                {settings[`${contentType}ReminderTeamsAttempt`]?.toggle &&
                                  <div>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          color="primary"
                                          checked={reminder.teamsRange !== "none"}
                                          onChange={(ev, checked) => this.onChangeReminderChannel(checked, index, "teams")}
                                        />
                                      }
                                      label="MS Teams Chatbot"
                                    />
                                  </div>
                                }
                                {this.props.smsManagementEnabled && settings[`${contentType}ReminderSMSAttempt`]?.toggle &&
                                  <div>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          color="primary"
                                          checked={reminder.smsRange !== "none"}
                                          onChange={(ev, checked) => this.onChangeReminderChannel(checked, index, "sms")}
                                        />
                                      }
                                      label="SMS"
                                    />
                                  </div>
                                }
                                {
                                  isCustomChannelAndNoReminderOptionsAreOn(reminder) &&
                                  <div className='select-channel-text-div'>
                                      <span className="select-channel-text">Select at least one (1) channel</span>
                                  </div>
                                }
                              </div>
                            </Collapse>
                          </div>
                        </div>
                      </Paper>
                      {index !== 0 &&
                        <IconButton size="small" onClick={() => this.onDeleteReminder(index)}><DeleteIcon fontSize="small" /></IconButton>
                      }
                    </Collapse>
                  )}
                  {!!this.state.reminders.length && this.state.reminders.length < 5 &&
                    <Button variant="text" color="primary" size="small" startIcon={<AddIcon fontSize="small" />} disabled={!this.state.reminders[this.state.reminders.length - 1].delay} onClick={this.onAddReminder}>Add Reminder</Button>
                  }
                </div>
              </div>
            </Collapse>
          </Paper>
        </div>
      </Drawer>
    );
  }

  private getDefaultReminder = (): ReminderOption => {
    const { contentType } = this.props;
    const defaultReminder = this.props.notificationSettings[`default${contentType.charAt(0).toUpperCase() + contentType.substring(1)}Reminder`] as number;

    const reminder: number = defaultReminder || 1;
    const delay: number = reminder % 24 === 0 ? reminder / 24 : reminder;
    const delayType: DelayType = reminder > 0 && reminder % 24 === 0 ? "days" : "hours";

    const isCustomChannel = getNotificationChannelsStrings(this.props.notifications).length === 0;

    let reminderOption: ReminderOption = {
      customChannels: isCustomChannel,
      delay: delay,
      delayType: delayType,
      emailRange: "none",
      mobileRange: "none",
      smsRange: "none",
      teamsRange: "none"
    };

    return reminderOption;
  }

  private getReminders = (): ReminderOption[] => {
    if (!!this.props.notifications && !!this.props.notifications.reminders) {
      if (!this.props.notifications.reminders.length)
        return [this.getDefaultReminder()];
      
      let reminders: ReminderOption[] = [];
      this.props.notifications.reminders.map((reminder, index) => {
        const delay: number = reminder.delay % 24 === 0 ? reminder.delay / 24 : reminder.delay;
        const delayType: DelayType = reminder.delay > 0 && reminder.delay % 24 === 0 ? "days" : "hours";

        /** Parse out "default" channels if returned from the API */
        if (reminder.channel as string === "default")
          return reminder;

        if (reminder.channel === "inherit" && getNotificationChannelsStrings(this.props.notifications).length > 0) {
          reminders.push({
            customChannels: false,
            delay: delay,
            delayType: delayType,
            emailRange: "none",
            mobileRange: "none",
            smsRange: "none",
            teamsRange: "none"
          });
        } 
        else {
          if (index > 0 && (reminders[reminders.length - 1].delay === delay && reminders[reminders.length - 1].delayType === delayType)) {
            if (reminder.channel === "email") {
              reminders[reminders.length - 1].emailRange = reminder.range;
            } 
            else if (reminder.channel === "mobile") {
              reminders[reminders.length - 1].mobileRange = reminder.range;
            } 
            else if (reminder.channel === "sms") {
              reminders[reminders.length - 1].smsRange = reminder.range;
            } 
            else if (reminder.channel === "teams") {
              reminders[reminders.length - 1].teamsRange = reminder.range;
            }
          } 
          else {
            reminders.push({
              customChannels: true,
              delay: delay,
              delayType: delayType,
              emailRange: reminder.channel === "email" ? reminder.range : "none",
              mobileRange: reminder.channel === "mobile" ? reminder.range : "none",
              smsRange: reminder.channel === "sms" ? reminder.range : "none",
              teamsRange: reminder.channel === "teams" ? reminder.range : "none"
            });
          }
        }
        return reminder;
      });

      return reminders;
    }

    return [this.getDefaultReminder()];
  }


  private onAddReminder = () => {
    this.setState({ reminders: this.state.reminders.concat([ this.getDefaultReminder() ]) })
  }

  private onApplyReminderSettings = () => {
    let reminders: Reminder[] = [];

    this.state.reminders
      .filter((reminder) => 
        !!reminder.delay && reminder.delay > 0 &&
        // if we choose custom channels, there must be a selection for one of the options or else the reminders won't be attached.
        !isCustomChannelAndNoReminderOptionsAreOn(reminder)
      )
      .forEach((reminder) => {
        const delay: number = reminder.delayType === "hours" ? reminder.delay! : reminder.delay! * 24;

        if (!reminder.customChannels) {
          reminders.push({ channel: "inherit", delay, range: "subscribers" });
        } 
        else {
          if (reminder.emailRange !== "none")
            reminders.push({ channel: "email", delay, range: reminder.emailRange });
          if (reminder.mobileRange !== "none")
            reminders.push({ channel: "mobile", delay, range: reminder.mobileRange });
          if (reminder.smsRange !== "none")
            reminders.push({ channel: "sms", delay, range: reminder.smsRange });
          if (reminder.teamsRange !== "none")
            reminders.push({ channel: "teams", delay, range: reminder.teamsRange });
        }
        return;
      });

    this.props.onChangeNotifications({ ...this.props.notifications!, reminders });
  }
  
  private onChangeReminderChannel = (checked: boolean, reminderIndex: number, channel: ReminderChannel) => {
    const reminders: ReminderOption[] = this.state.reminders.map((reminder, index) => {
      if (index === reminderIndex) {
        if (channel === "email")
          return { ...reminder, emailRange: checked ? "subscribers" : "none" };
        else if (channel === "mobile")
          return { ...reminder, mobileRange: checked ? "subscribers" : "none" };
        else if (channel === "sms")
          return { ...reminder, smsRange: checked ? "subscribers" : "none" };
        else if (channel === "teams")
          return { ...reminder, teamsRange: checked ? "subscribers" : "none" };
        return reminder;
      }
      return reminder;
    });

    this.setState({ reminders });
  }

  private onChangeReminderDelay = (reminderIndex: number, delay: number) => {
    const reminders: ReminderOption[] = this.state.reminders.map((reminder, index) => {
      if (index === reminderIndex)
        return { ...reminder, delay: isNaN(delay) ? 1 : delay };
      return reminder;
    });

    this.setState({ reminders });
  }

  private onChangeReminderDelayType = (reminderIndex: number, delayType: DelayType) => {
    const reminders: ReminderOption[] = this.state.reminders.map((reminder, index) => {
      if (index === reminderIndex)
        return { ...reminder, delayType };
      return reminder;
    });

    this.setState({ reminders });
  }

  private onClose = () => {
    this.onApplyReminderSettings();
    this.props.onClose();
  }

  private onDeleteReminder = (reminderIndex: number) => {
    let reminders: ReminderOption[] = this.state.reminders.slice();
    reminders.splice(reminderIndex, 1);
    this.setState({ reminders });
  }

  private onSetCustomReminderChannels = (reminderIndex: number) => {
    const reminders: ReminderOption[] = this.state.reminders.map((reminder, index) => {
      if (index === reminderIndex)
        return { ...reminder, customChannels: true };
      return reminder;
    });

    this.setState({ reminders });
  }

  private onSetNotificationReminderChannels = (reminderIndex: number) => {
    const reminders: ReminderOption[] = this.state.reminders.map((reminder, index) => {
      if (index === reminderIndex)
        return { ...reminder, customChannels: false };
      return reminder;
    });
    
    this.setState({ reminders });
  }

  private getInheritChannelsLabel = (reminderOption: ReminderOption): React.ReactNode => {
    const reminderChannelsStrings = getNotificationChannelsStrings(this.props.notifications);
    return <>
      {
        !reminderOption.customChannels &&
        <div className='reminder-inherit-channels-label'>
          Reminder channel{reminderChannelsStrings.length > 1 ? 's' : ''}: {reminderChannelsStrings.join(', ')}
        </div>
      }
    </>
  }
}

interface IRemindersProps {
  show: boolean;
  isPreviouslyPublished: boolean;
  notifications: Notifications | undefined;
  contentType: "post" | "event";
  isCompliance?: boolean;
  onChangeNotifications: (notifications: Notifications) => void;
  onClose: () => void;
}

interface ComponentState {
  reminders: ReminderOption[];
  isNoAuthoringNotificationChannelsOn: boolean;
}

const connector = connect(
  (state: GlobalApplicationState, ownProps: IRemindersProps) => ({
    ...ownProps,
    notificationSettings: state.settings.notificationSettings,
    tenantSettings: state.settings.tenantSettings,
    smsManagementEnabled: state.settings.tenantSettings.showFeatures.smsManagementEnabled
  })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(Reminders);